import api from './api';
import type { CodigoServico } from './codigosServicoService';

export type Segmento =
  | 'Ensino Infantil'
  | 'Ensino Fund. Anos Iniciais'
  | 'Ensino Fund. Anos Finais'
  | 'Ensino Médio'
  | 'N/A';

export const SEGMENTOS: Segmento[] = [
  'Ensino Infantil',
  'Ensino Fund. Anos Iniciais',
  'Ensino Fund. Anos Finais',
  'Ensino Médio',
  'N/A',
];

export interface Serie {
  id: number;
  nome: string;
  tipoEnsino: 'curricular' | 'extracurricular';
  segmento: Segmento;
  valorMensalidade: number;
  valorMaterialDidatico: number;
  ativa: boolean;
  ordem: number;
  codigoServicoId?: number | null;
  codigoServico?: CodigoServico | null;
  criadaEm: string;
  atualizadaEm: string;
}

export interface CreateSerieDto {
  nome: string;
  tipoEnsino: 'curricular' | 'extracurricular';
  segmento?: Segmento;
  valorMensalidade: number;
  valorMaterialDidatico?: number;
  ordem?: number;
  codigoServicoId: number;
}

export interface UpdateSerieDto {
  nome?: string;
  tipoEnsino?: 'curricular' | 'extracurricular';
  segmento?: Segmento;
  valorMensalidade?: number;
  valorMaterialDidatico?: number;
  ordem?: number;
  codigoServicoId?: number;
}

type RawSerie = {
  id: number;
  nome: string;
  tipoEnsino?: 'curricular' | 'extracurricular';
  tipo_ensino?: 'curricular' | 'extracurricular';
  segmento?: Segmento;
  valorMensalidade?: number;
  valorMaterialDidatico?: number;
  valor?: number;
  ativa: boolean;
  ordem?: number;
  codigoServicoId?: number | null;
  codigoServico?: CodigoServico | null;
  criadaEm?: string;
  created_at?: string;
  atualizadaEm?: string;
  updated_at?: string;
};

const normalizeSerie = (raw: RawSerie): Serie => ({
  id: raw.id,
  nome: raw.nome,
  tipoEnsino: raw.tipoEnsino ?? raw.tipo_ensino ?? 'curricular',
  segmento: raw.segmento ?? 'N/A',
  valorMensalidade: raw.valorMensalidade ?? raw.valor ?? 0,
  valorMaterialDidatico: raw.valorMaterialDidatico ?? 0,
  ativa: raw.ativa,
  ordem: raw.ordem ?? 0,
  codigoServicoId: raw.codigoServicoId ?? null,
  codigoServico: raw.codigoServico ?? null,
  criadaEm: raw.criadaEm ?? raw.created_at ?? new Date().toISOString(),
  atualizadaEm: raw.atualizadaEm ?? raw.updated_at ?? new Date().toISOString(),
});

export const seriesService = {
  // Listar todas as séries
  async getAll(): Promise<Serie[]> {
    const response = await api.get<RawSerie[]>('/series');
    return response.data.map(normalizeSerie);
  },

  // Buscar série por ID
  async getById(id: number): Promise<Serie> {
    const response = await api.get<RawSerie>(`/series/${id}`);
    return normalizeSerie(response.data);
  },

  // Listar apenas séries ativas (para dropdowns)
  async getAtivas(): Promise<Serie[]> {
    try {
      const response = await api.get<RawSerie[]>('/series/ativas');
      return response.data.map(normalizeSerie);
    } catch {
      const all = await this.getAll();
      return all.filter(serie => serie.ativa);
    }
  },

  // Listar séries por tipo de ensino
  async getByTipo(tipo: 'curricular' | 'extracurricular'): Promise<Serie[]> {
    const response = await api.get<RawSerie[]>(`/series/tipo/${tipo}`);
    return response.data.map(normalizeSerie);
  },

  // Criar nova série
  async create(data: CreateSerieDto): Promise<Serie> {
    const response = await api.post<RawSerie>('/series', data);
    return normalizeSerie(response.data);
  },

  // Atualizar série
  async update(id: number, data: UpdateSerieDto): Promise<Serie> {
    const response = await api.patch<RawSerie>(`/series/${id}`, data);
    return normalizeSerie(response.data);
  },

  // Ativar/Desativar série
  async toggleAtiva(id: number): Promise<Serie> {
    const response = await api.patch<RawSerie>(`/series/${id}/toggle-ativa`);
    return normalizeSerie(response.data);
  },

  // Remover série
  async delete(id: number): Promise<void> {
    await api.delete(`/series/${id}`);
  },

  // Verificar se pode desativar (sem turmas ativas)
  async canDeactivate(id: number): Promise<{ canDeactivate: boolean; activeTurmas: number }> {
    try {
      const response = await api.get(`/series/${id}/can-deactivate`);
      return response.data;
    } catch {
      // Se endpoint não existir, permite desativar
      return { canDeactivate: true, activeTurmas: 0 };
    }
  }
};
